import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, Button, Input } from './ui';
import { api } from '../lib/api';
import DuplicatePropertyDialog from './DuplicatePropertyDialog';
import type { BrochurePrefill } from './BrochureGeneratorDialog';
import {
  createSubmitLock, duplicateCandidatesFrom, friendlyErrorMessage,
  type DuplicateAction, type DuplicateCandidate,
} from '../utils/duplicateChoice';

/**
 * The deal's property, or the doors to attach one (NN0).
 *
 * A deal may point at the Property Database record (`masterPropertyId`), at the analyzer row
 * (`propertyId`), both, or neither. The facts come through the brochure prefill, which already
 * resolves either id - zero new read endpoints. With neither: search the database and pick, or
 * upload an OM (the Upload page comes back here with the new row). "Enter a listing by hand"
 * is deliberately absent - there is no form to send him to (backlog).
 */
interface Props {
  dealId: string;
  propertyId: string | null;
  masterPropertyId: string | null;
  /** Called after an attach succeeds so the deal page reloads its pointers. */
  onAttached: () => void;
}

interface MasterHit { id: string; address: string | null; city: string | null; state?: string | null }

export default function DealPropertyPanel({ dealId, propertyId, masterPropertyId, onAttached }: Props) {
  const anyId = masterPropertyId ?? propertyId;
  const [prefill, setPrefill] = useState<BrochurePrefill | null>(null);
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<MasterHit[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicates, setDuplicates] = useState<{ candidates: DuplicateCandidate[]; masterId: string } | null>(null);
  const lock = useRef(createSubmitLock());

  useEffect(() => {
    if (!anyId) { setPrefill(null); return; }
    let live = true;
    api.getBrochurePrefill(anyId)
      .then((r: any) => { if (live) setPrefill(r.prefill as BrochurePrefill); })
      .catch(() => { if (live) setPrefill(null); });
    return () => { live = false; };
  }, [anyId]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 3) { setHits([]); return; }
    let live = true;
    const t = setTimeout(() => {
      api.searchMasterProperties(q)
        .then((r: any) => { if (live) setHits((r.properties ?? r.data ?? []) as MasterHit[]); })
        .catch(() => { if (live) setHits([]); });
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [query]);

  const attach = async (body: { master_property_id?: string; property_id?: string; force_create?: true }) => {
    // Synchronous lock before any await: two clicks in one tick would both see busy=false.
    if (!lock.current.tryAcquire()) return;
    setBusy(true); setError(null);
    try {
      await api.attachDealProperty(dealId, body);
      setDuplicates(null);
      onAttached();
    } catch (err) {
      const candidates = duplicateCandidatesFrom(err);
      if (candidates && body.master_property_id) { setDuplicates({ candidates, masterId: body.master_property_id }); return; }
      setError(friendlyErrorMessage(err, 'Could not attach the property'));
    } finally {
      setBusy(false);
      lock.current.release();
    }
  };

  const onDuplicateChoice = (action: DuplicateAction, c: DuplicateCandidate) => {
    if (!duplicates) return;
    if (action === 'open') { void attach({ property_id: c.id }); return; }
    if (action === 'create_separate') { void attach({ master_property_id: duplicates.masterId, force_create: true }); }
  };

  const facts = prefill?.suggestedFacts ?? [];
  // The prefill's fact list already carries the APN when the record has one (measured on the
  // Aguilera deal: it printed twice). Add our own row only when the list does not.
  const apnRow = prefill?.apn && !facts.some((f) => /^APN/i.test(f.label)) ? prefill.apn : null;
  const hero = prefill?.photos?.hero?.src ?? null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{anyId ? 'Property' : 'Attach a property'}</CardTitle>
      </CardHeader>
      <CardContent>
        {error && <div className="mb-3 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>}

        {anyId ? (
          <div className="flex gap-4">
            {hero && (
              <img src={hero} alt="" className="w-40 h-28 object-cover rounded-lg border shrink-0" />
            )}
            <div className="min-w-0">
              <p className="font-medium text-gray-900">{prefill?.addressLine ?? 'Property'}</p>
              <p className="text-sm text-gray-500">{[prefill?.city, prefill?.state, prefill?.zip].filter(Boolean).join(', ')}</p>
              {prefill?.photos?.heroIsStreetView && <p className="text-[11px] text-warning mt-0.5">Photo is a Street View capture.</p>}
              {(facts.length > 0 || apnRow) && (
                <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-0.5 text-sm">
                  {facts.map((f) => (
                    <div key={f.label} className="contents"><dt className="text-gray-500">{f.label}</dt><dd className="text-gray-900">{f.value}</dd></div>
                  ))}
                  {apnRow && <div className="contents"><dt className="text-gray-500">APN:</dt><dd className="text-gray-900">{apnRow}</dd></div>}
                </dl>
              )}
              <div className="flex flex-wrap items-center gap-2 mt-3">
                {masterPropertyId && <Link to={`/data-hub/${masterPropertyId}`} className="text-sm text-primary-500 hover:underline">Property Database record</Link>}
                {propertyId && <Link to={`/properties/${propertyId}`} className="text-sm text-primary-500 hover:underline">Analyzer property</Link>}
                {!propertyId && masterPropertyId && (
                  <Button size="sm" onClick={() => { void attach({ master_property_id: masterPropertyId }); }} disabled={busy} isLoading={busy}>
                    Bring into the analyzer
                  </Button>
                )}
              </div>
              {!propertyId && masterPropertyId && (
                <p className="text-xs text-gray-500 mt-1">Creates the working row in My Deals so Analyze, Comps and AI Valuation can run.</p>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <Input
              placeholder="Search the Property Database by address, name or city"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {hits.length > 0 && (
              <ul className="divide-y border rounded-lg">
                {hits.map((h) => (
                  <li key={h.id} className="flex items-center justify-between gap-2 px-3 py-2">
                    <span className="text-sm text-gray-900 truncate">{[h.address, h.city].filter(Boolean).join(', ')}</span>
                    <Button size="sm" variant="outline" disabled={busy} onClick={() => { void attach({ master_property_id: h.id }); }} aria-label={`Attach ${h.address ?? h.id}`}>
                      Attach
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <Link to={`/upload?deal=${dealId}`}><Button size="sm" variant="outline">Upload an OM</Button></Link>
              <span className="text-xs text-gray-500">The extracted property comes back to this deal.</span>
            </div>
          </div>
        )}
      </CardContent>

      {duplicates && (
        <DuplicatePropertyDialog
          candidates={duplicates.candidates}
          busy={busy}
          onChoose={onDuplicateChoice}
          onCancel={() => setDuplicates(null)}
        />
      )}
    </Card>
  );
}
